export const POUNDS_PER_SHORT_TON = 2000;

export const LANDFILL_TIPPING_FEE = {
  usdPerShortTon: 64.48,
  basis: "EREF 2025 national average MSW landfill tipping fee, unweighted",
  sourceUrl:
    "https://erefdn.org/product/2025-analysis-of-municipal-solid-waste-msw-landfill-tipping-fees/",
} as const;

export const WARM_SOURCE = {
  name: "EPA Waste Reduction Model (WARM)",
  version: "Version 16",
  released: "December 2023",
  documentationUrl:
    "https://www.epa.gov/warm/documentation-chapters-greenhouse-gas-emission-energy-and-economic-factors-used-waste",
} as const;

export const WARM_MATERIAL_KEYS = [
  "corrugated-containers",
  "dimensional-lumber",
  "hdpe",
  "steel-cans",
] as const;

export type WarmMaterialKey = (typeof WARM_MATERIAL_KEYS)[number];

export interface WarmMaterialFactor {
  warmMaterial: string;
  divertedPathway: "recycling" | "reuse";
  divertedMtco2ePerShortTon: number | null;
  landfilledMtco2ePerShortTon: number;
  chapter: string;
  exhibit: string;
}

export const WARM_MATERIAL_FACTORS: Record<WarmMaterialKey, WarmMaterialFactor> = {
  "corrugated-containers": {
    warmMaterial: "Corrugated Containers",
    divertedPathway: "recycling",
    divertedMtco2ePerShortTon: -3.14,
    landfilledMtco2ePerShortTon: 0.18,
    chapter: "Containers, Packaging and Non-Durable Goods Materials: Paper Products",
    exhibit: "Exhibit 3-7, Net Emissions for Paper Products under Each Materials Management Option",
  },
  "dimensional-lumber": {
    warmMaterial: "Dimensional Lumber",
    divertedPathway: "reuse",
    divertedMtco2ePerShortTon: -1.66,
    landfilledMtco2ePerShortTon: -0.92,
    chapter: "Construction Materials: Wood Products",
    exhibit: "Exhibit 12-3, Net Emissions for Wood Products under Each Materials Management Option",
  },
  hdpe: {
    warmMaterial: "HDPE",
    divertedPathway: "recycling",
    divertedMtco2ePerShortTon: -0.76,
    landfilledMtco2ePerShortTon: 0.02,
    chapter: "Containers, Packaging and Non-Durable Goods Materials: Plastics",
    exhibit: "Exhibit 5-3, Net Emissions for Plastics under Each Materials Management Option",
  },
  "steel-cans": {
    warmMaterial: "Steel Cans",
    divertedPathway: "recycling",
    divertedMtco2ePerShortTon: -1.83,
    landfilledMtco2ePerShortTon: 0.02,
    chapter: "Containers, Packaging and Non-Durable Goods Materials: Metals",
    exhibit: "Exhibit 2-7, Net Emissions for Metals under Each Materials Management Option",
  },
};

export function warmReductionPerShortTon(material: WarmMaterialKey): number | null {
  const factor = WARM_MATERIAL_FACTORS[material];
  if (factor.divertedMtco2ePerShortTon === null) return null;
  const reduction =
    factor.landfilledMtco2ePerShortTon - factor.divertedMtco2ePerShortTon;
  return Math.round(reduction * 100) / 100;
}

export const PACKAGING_IMPACT_TYPES = [
  "wood-pallets",
  "plastic-pallets",
  "gaylord-boxes",
  "ibc-totes",
  "steel-drums",
  "plastic-drums",
] as const;

export type PackagingImpactType = (typeof PACKAGING_IMPACT_TYPES)[number];

export interface PackagingUnitProfile {
  label: string;
  unitNoun: string;
  typicalEmptyWeightLbs: number;
  weightBasis: string;
  weightSourceUrl: string;
  warmMaterial: WarmMaterialKey | null;
}

export const PACKAGING_UNIT_PROFILES: Record<
  PackagingImpactType,
  PackagingUnitProfile
> = {
  "wood-pallets": {
    label: "Wood pallets",
    unitNoun: "pallets",
    typicalEmptyWeightLbs: 40,
    weightBasis: "48×40 GMA wood pallet, listed at 38–42 lb empty",
    weightSourceUrl: "https://floridapalletsupply.com/48x40-pallets/",
    warmMaterial: "dimensional-lumber",
  },
  "plastic-pallets": {
    label: "Plastic pallets",
    unitNoun: "pallets",
    typicalEmptyWeightLbs: 30,
    weightBasis: "48×40 HDPE pallet, listed at 28–32 lb empty",
    weightSourceUrl: "https://floridapalletsupply.com/48x40-pallets/",
    warmMaterial: "hdpe",
  },
  "gaylord-boxes": {
    label: "Gaylord boxes",
    unitNoun: "boxes",
    typicalEmptyWeightLbs: 15,
    weightBasis: "48×40×36 double-wall gaylord with lid (Uline S-4480), 15 lb",
    weightSourceUrl:
      "https://www.uline.com/Product/Detail/S-4480/Bulk-Cargo/48-x-40-x-36-Double-Wall-Gaylord-Box-with-Lid",
    warmMaterial: "corrugated-containers",
  },
  "ibc-totes": {
    label: "IBC totes",
    unitNoun: "totes",
    typicalEmptyWeightLbs: 135,
    weightBasis: "275-gallon poly caged IBC, 135 lb empty",
    weightSourceUrl:
      "https://www.ibctanks.com/knowledge-base/how-heavy-is-an-empty-ibc-tote",
    warmMaterial: null,
  },
  "steel-drums": {
    label: "Steel drums",
    unitNoun: "drums",
    typicalEmptyWeightLbs: 46,
    weightBasis: "55-gallon 18-gauge open-head steel drum, 46 lb",
    weightSourceUrl: "https://www.thecarycompany.com/55-gallon-steel-drum-26w55l",
    warmMaterial: "steel-cans",
  },
  "plastic-drums": {
    label: "Plastic drums",
    unitNoun: "drums",
    typicalEmptyWeightLbs: 21.5,
    weightBasis: "55-gallon HDPE tight-head drum, nominal tare 21.5 lb",
    weightSourceUrl:
      "https://www.thecarycompany.com/55-gallon-tight-head-plastic-drum-56w55d",
    warmMaterial: "hdpe",
  },
};

export function shortTonsFromPounds(pounds: number): number {
  return pounds / POUNDS_PER_SHORT_TON;
}

export interface PackagingImpactInput {
  productType: PackagingImpactType;
  monthlyUnits: number;
  weightPerUnitLbs: number;
}

export interface PackagingImpactEstimate {
  monthlyPounds: number;
  monthlyShortTons: number;
  annualShortTons: number;
  monthlyMtco2eAvoided: number | null;
  annualMtco2eAvoided: number | null;
}

function assertNonNegative(name: string, value: number): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative number`);
  }
}

export function estimatePackagingImpact({
  productType,
  monthlyUnits,
  weightPerUnitLbs,
}: PackagingImpactInput): PackagingImpactEstimate {
  assertNonNegative("monthlyUnits", monthlyUnits);
  assertNonNegative("weightPerUnitLbs", weightPerUnitLbs);
  const monthlyPounds = monthlyUnits * weightPerUnitLbs;
  const monthlyShortTons = shortTonsFromPounds(monthlyPounds);
  const { warmMaterial } = PACKAGING_UNIT_PROFILES[productType];
  const reductionPerShortTon =
    warmMaterial === null ? null : warmReductionPerShortTon(warmMaterial);
  const monthlyMtco2eAvoided =
    reductionPerShortTon === null ? null : monthlyShortTons * reductionPerShortTon;
  return {
    monthlyPounds,
    monthlyShortTons,
    annualShortTons: monthlyShortTons * 12,
    monthlyMtco2eAvoided,
    annualMtco2eAvoided:
      monthlyMtco2eAvoided === null ? null : monthlyMtco2eAvoided * 12,
  };
}

export function modelledPathway(
  productType: PackagingImpactType,
): WarmMaterialFactor["divertedPathway"] | null {
  const { warmMaterial } = PACKAGING_UNIT_PROFILES[productType];
  if (warmMaterial === null || warmReductionPerShortTon(warmMaterial) === null) return null;
  return WARM_MATERIAL_FACTORS[warmMaterial].divertedPathway;
}
